import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import AITranslations from "./AITranslations";   

const DEFAULT_STATS = {
  criticalSignals: 0,
  mediumSignals: 0,
  lowSignals: 0,
  totalSignals: 0,
};

const HAFHAI = ({ selectedLanguage, role }) => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [userRole, setUserRole] = useState(role);
  const [module, setModule] = useState("HAFH");
  const [signals, setSignals] = useState([]);
  const [summary, setSummary] = useState("");
  const [stats, setStats] = useState(DEFAULT_STATS);

  const [agentStatus, setAgentStatus] = useState("");
  const [actions, setActions] = useState([]);
  const [agentState, setAgentState] = useState(null);
  const [showAgentPopup, setShowAgentPopup] = useState(false);
  const [agentPipeline, setAgentPipeline] = useState([]);
  const [toolExecutions, setToolExecutions] = useState([]);

  const [memoryTimeline, setMemoryTimeline] = useState([]);
  const [reflections, setReflections] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const t = (key) => AITranslations[selectedLanguage]?.[key] || key;

  const prevStatusRef = useRef(null);

  useEffect(() => {
    if (
      agentState?.status &&
      agentState.status !== prevStatusRef.current
    ) {
      setShowAgentPopup(true);

      setTimeout(() => setShowAgentPopup(false), 2000);

      prevStatusRef.current = agentState.status;
    }
  }, [agentState]);

  useEffect(() => {
    const interval =
      setInterval(async () => {
        const res = await fetch(
          "http://localhost:3001/api/agent/snapshot"
        );
        const data =
          await res.json();
        setStats({
          criticalSignals:
            data.criticalSignals || 0,

          mediumSignals:
            data.mediumSignals || 0,

          lowSignals:
            data.lowSignals || 0,

          totalSignals:
            (data.criticalSignals || 0)
            +
            (data.mediumSignals || 0),
        });
        setSummary(data.summary);
        setAgentState(data);
      }, 10000);

    return () =>
      clearInterval(interval);

  }, []);

  const fetchAIInsights = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        "http://localhost:3001/api/ai/query",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            role: userRole,
            query: `${userRole} cognitive risk analysis`,
          }),
        }
      );

      if (!response.ok) throw new Error("AI request failed");

      const data = await response.json();

      // SIGNALS (RAG OR FALLBACK)
      setSignals(data.signals || data.cognition?.signals || []);

      // SUMMARY
      setSummary(data.summary || data.cognition?.summary || "");

      // STATS
      setStats({
        criticalSignals:
          data.criticalSignals ??
          data.cognition?.criticalSignals ??
          0,

        mediumSignals:
          data.mediumSignals ??
          data.cognition?.mediumSignals ??
          0,

        lowSignals:
          data.lowSignals ??
          data.cognition?.lowSignals ??
          0,

        totalSignals:
          data.totalSignals ??
          data.cognition?.totalSignals ??
          0,
      });

      // AGENT LAYER (SAFE HANDLING)
      setAgentStatus(data.status || "");
      setActions(data.actions || []);
    } catch (err) {
      console.error(err);
      setError("Failed to load cognitive insights");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAIInsights();
  }, [userRole]);

  const severityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case "high":
        return "text-red-400";
      case "medium":
        return "text-yellow-400";
      case "low":
        return "text-green-400";
      default:
        return "text-gray-400";
    }
  };

  const safeText = (value) => {
    if (!value) return "N/A";
    if (typeof value === "string") return value;
    return value?.[selectedLanguage] || value?.en || "N/A";
  };

  const cognitiveHealth = useMemo(() => {
    if (stats.criticalSignals >= 3) {
      return {
        label: "Critical Cognitive State",
        color: "bg-red-900",
      };
    }

    if (stats.criticalSignals >= 1) {
      return {
        label: "Elevated Cognitive Risk",
        color: "bg-yellow-800",
      };
    }

    return {
      label: "Stable Cognitive State",
      color: "bg-green-800",
    };
  }, [stats]);

  return (
    <div className="bg-gray-900 rounded-2xl p-4 text-white space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm lg:text-lg font-semibold text-yellow-200">
            {t("cogsiggraph")} 
            <span className="text-gray-200 mr-1"> {userName} </span> {t("as")}
            <span className="text-gray-200 ml-2 mr-1">{userRole ? t(userRole.toLowerCase()) : "N/A"}</span> 
          </h2>
          <p className="text-xs text-cyan-200 mt-1">
            {t("realtimeai")}
          </p>
        </div>
      </div>

      {loading && (
        <div className="bg-gray-800 p-4 rounded-xl text-sm animate-pulse">
          Thinking cognitively...
        </div>
      )}
      {error && (
        <div className="bg-red-900 p-4 rounded-xl text-sm">
          {error}
        </div>
      )}

      {/* COGNITIVE HEALTH */}
      {!loading && (
        <div className={`${cognitiveHealth.color} p-4 rounded-xl`}>
          <div className="text-sm text-gray-200">
            Cognitive Status
          </div>
          <div className="text-lg font-semibold mt-1">
            {cognitiveHealth.label}
          </div>
        </div>
      )}

      {agentState && (
        <div className="bg-blue-950 p-3 rounded-xl text-xs space-y-1">
          <div className="text-yellow-300 font-bold">
            Agent Live State
          </div>

          <div>Status: {agentState.status}</div>
          <div>{agentState.summary}</div>
          <div className="text-gray-400">
            Updated: {agentState.lastUpdated}
          </div>
        </div>
      )}

      {showAgentPopup && agentState && (
        <div className="fixed top-4 right-4 bg-blue-900 text-white p-3 rounded-xl shadow-lg z-50 animate-pulse">
          <div className="text-xs text-yellow-300">
            🤖 AGENT ACTIVE
          </div>

          <div className="text-sm font-bold">
            {agentState.status}
          </div>

          <div className="text-xs text-gray-300">
            {agentState.message}
          </div>
        </div>
      )}

      {/* AGENT STATUS */}
      {agentStatus && (
        <div className="bg-blue-900 p-2 rounded text-xs">
          Agent Status: {agentStatus}
        </div>
      )}

      {/* AGENT ACTIONS */}
      {actions?.length > 0 && (
        <div className="bg-black p-3 rounded">
          <h3 className="text-yellow-300 text-xs">
            Agent Actions
          </h3>

          {actions.map((a, i) => (
            <div key={i} className="text-green-300 text-xs mt-1">
              {a.type} → {a.message}
            </div>
          ))}
        </div>
      )}

      {agentPipeline?.length > 0 && (
        <div className="bg-gray-950 p-4 rounded-xl">
          <h3 className="text-yellow-300 text-sm">
            Agent Cognitive Pipeline
          </h3>

          <div className="flex flex-wrap gap-2 mt-3">
            {agentPipeline.map((step, idx) => (
              <div
                key={idx}
                className={`px-3 py-2 rounded-full text-xs
                ${
                  step.status === "completed"
                    ? "bg-green-700"
                    : step.status === "running"
                    ? "bg-blue-700 animate-pulse"
                    : "bg-gray-700"
                }`}
              >
                {step.step.toUpperCase()}
              </div>
            ))}
          </div>
        </div>
      )}

      {toolExecutions?.length > 0 && (
        <div className="bg-indigo-950 p-4 rounded-xl">
          <h3 className="text-cyan-300 text-sm font-semibold">
            Autonomous Tool Execution
          </h3>

          <div className="space-y-2 mt-3">
            {toolExecutions.map((tool, idx) => (
              <div
                key={idx}
                className="bg-black/40 rounded-lg p-3"
              >
                <div className="flex justify-between">
                  <div className="text-cyan-200 text-xs font-bold">
                    {tool.tool}
                  </div>

                  <div className="text-[10px] text-green-400">
                    {tool.status}
                  </div>
                </div>

                <div className="text-xs text-gray-300 mt-1">
                  {tool.message}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {memoryTimeline?.length > 0 && (
        <div className="bg-purple-950 p-4 rounded-xl">
          <h3 className="text-pink-300 text-sm">
            Cognitive Memory Timeline
          </h3>

          <div className="space-y-3 mt-3">
            {memoryTimeline.map((m, idx) => (
              <div
                key={idx}
                className="border-l-2 border-pink-500 pl-3"
              >
                <div className="text-[10px] text-gray-400">
                  {m.date}
                </div>

                <div className="text-sm text-white">
                  {m.event}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {reflections?.length > 0 && (
        <div className="bg-orange-950 p-4 rounded-xl">
          <h3 className="text-orange-300 text-sm">
            Agent Reflection Loop
          </h3>

          <div className="space-y-3 mt-3">
            {reflections.map((r, idx) => (
              <div
                key={idx}
                className="bg-black/30 p-3 rounded-lg"
              >
                <div className="text-yellow-200 text-xs font-semibold">
                  {r.question}
                </div>

                <div className="text-sm text-gray-300 mt-1">
                  {r.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SUMMARY */}
      {!!summary && (
        <div className="bg-purple-900 p-4 rounded-xl">
          <h3 className="text-yellow-300 text-sm">
            Cognitive Summary
          </h3>
          <p className="text-sm mt-2 text-white">
            {summary}
          </p>
        </div>
      )}

      {/* STATS */}
      <div className="grid grid-cols-4 gap-1">
        <div className="bg-red-800 rounded-xl p-3">
          <div className="text-2xl font-bold">
            {stats.criticalSignals}
          </div>
          <div className="text-xs mt-1">Critical</div>
        </div>

        <div className="bg-yellow-700 rounded-xl p-3">
          <div className="text-2xl font-bold">
            {stats.mediumSignals}
          </div>
          <div className="text-xs mt-1">Medium</div>
        </div>

        <div className="bg-green-700 rounded-xl p-3">
          <div className="text-2xl font-bold">
            {stats.lowSignals}
          </div>
          <div className="text-xs mt-1">Low</div>
        </div>

        <div className="bg-blue-700 rounded-xl p-3">
          <div className="text-2xl font-bold">
            {stats.totalSignals}
          </div>
          <div className="text-xs mt-1">Total</div>
        </div>
      </div>

      {/* SIGNALS */}
      <div className="space-y-3">
        {signals.length === 0 && !loading && (
          <div className="bg-gray-800 p-4 rounded-xl text-sm text-gray-400">
            All systems normal — no issues detected!
          </div>
        )}

        {signals.map((signal, index) => (
          <div
            key={index}
            className="bg-gray-800 rounded-xl p-4 border border-gray-700"
          >
            <div className="flex items-center justify-between">
              <div className="text-teal-300 font-medium">
                {safeText(signal.type)}
              </div>

              <div className={`text-xs font-semibold ${severityColor(signal.severity)}`}>
                {signal.severity}
              </div>
            </div>

            <p className="text-sm text-gray-300 mt-2">
              {safeText(signal.insight)}
            </p>

            <div className="text-[10px] text-gray-500 mt-3">
              {signal.source || "Cognitive AI"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HAFHAI;